import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Alert,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { extractErrorMessage } from '../utils/errorHandler';

const backgroundImageUrl = 'http://localhost:3845/assets/3842beb8df5520fd4674019652e302f1ff5b9cb0.png';
const iconEmail = 'http://localhost:3845/assets/7a0777f0ef1bbf5181f5895e6967da06a2fe3f45.svg';
const iconLock = 'http://localhost:3845/assets/69e004fd874116a80edd1550d27f34ede442d570.svg';
const iconCheck = 'http://localhost:3845/assets/76529fb74725c6a2423c9b55772ede4b618320a6.svg';
const iconData = 'http://localhost:3845/assets/8a93b3fd912c56119afce886b4b3397813c4a0af.svg';
const iconMoney = 'http://localhost:3845/assets/2f24539808aabf6fa3c878f3c2303ec55fed9224.svg';

const featuresList = [
  'Monitoramento em tempo real',
  'Inteligência de dados',
  'Gestão financeira integrada',
];

export const LoginPage: React.FC = () => {
  const { login, authState } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (authState.isAuthenticated && authState.hasActivePlan) {
      navigate('/dashboard', { replace: true });
    } else if (authState.isAuthenticated) {
      navigate('/subscription-required', { replace: true });
    }
  }, [authState.isAuthenticated, authState.hasActivePlan, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await login({
        email: email.toLowerCase().trim(),
        password: password,
        rememberMe,
      });

      if (!result.success) {
        setError(result.message || 'Erro ao fazer login.');
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f7faf9',
        p: { xs: 2, md: 0 },
      }}
    >
      <Box
        sx={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0px 10px 15px -3px rgba(0,0,0,0.1),0px 4px 6px -4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          maxWidth: 1440,
          width: '100%',
          minHeight: { xs: 'auto', md: '921.59px' },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box
          sx={{
            flex: 1,
            backgroundColor: '#003d4d',
            minHeight: { xs: 400, md: '100%' },
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            component="img"
            src={backgroundImageUrl}
            alt=""
            sx={{
              position: 'absolute',
              width: 1891,
              height: 2103,
              left: -1136,
              top: -1075.2,
              objectFit: 'cover',
              pointerEvents: 'none',
            }}
          />

          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: `url('data:image/svg+xml;utf8,<svg viewBox="0 0 620 921.59" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none"><g transform="matrix(-16.55 -54.23 54.23 -16.55 475.5 542.3)" opacity="0.699999988079071"><rect height="99.358" width="175.78" fill="url(%23grad)" id="quad" shape-rendering="crispEdges"/><use href="%23quad" transform="scale(1 -1)"/><use href="%23quad" transform="scale(-1 1)"/><use href="%23quad" transform="scale(-1 -1)"/></g><defs><linearGradient id="grad" gradientUnits="userSpaceOnUse" x2="5" y2="5"><stop stop-color="rgba(15,23,42,0.1)" offset="0"/><stop stop-color="rgba(15,23,42,1)" offset="0.81838"/></linearGradient></defs></svg>')`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
            }}
          />

          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              top: 0,
              width: { xs: '100%', md: 513 },
              p: 6,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              sx={{
                fontFamily: '"New Order", sans-serif',
                fontWeight: 700,
                fontSize: '2rem',
                color: '#ffffff',
                pb: 4.5,
              }}
            >
              MUU Agrotech
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mt: 6,
                maxWidth: 576,
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"New Order", sans-serif',
                  fontWeight: 600,
                  fontSize: '2.5rem',
                  color: '#ffffff',
                  lineHeight: 1.5,
                }}
              >
                Tecnologia inteligente
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"New Order", sans-serif',
                  fontWeight: 600,
                  fontSize: '2.5rem',
                  color: '#ffffff',
                  lineHeight: 1.5,
                }}
              >
                para a{' '}
                <Box component="span" sx={{ color: '#8cf3f3' }}>
                  Pecuária Moderna
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontFamily: '"New Order", sans-serif',
                  fontWeight: 400,
                  fontSize: '1.25rem',
                  color: '#ffffff',
                  lineHeight: 1.4,
                  mt: 1,
                }}
              >
                Gestão, rastreabilidade e inteligência de dados em uma única plataforma.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                mt: 'auto',
                pt: 8,
              }}
            >
              {featuresList.map((feature, index) => (
                <Box
                  key={index}
                  sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                >
                  <Box
                    component="img"
                    src={index === 0 ? iconCheck : index === 1 ? iconData : iconMoney}
                    alt=""
                    sx={{ width: 24, height: 24 }}
                  />
                  <Typography
                    sx={{
                      fontFamily: '"New Order", sans-serif',
                      fontWeight: 400,
                      fontSize: '1.125rem',
                      color: '#ffffff',
                    }}
                  >
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            backgroundColor: '#f7faf9',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 8,
            minHeight: { xs: 'auto', md: '100%' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              maxWidth: 448,
              width: '100%',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                sx={{
                  fontFamily: '"New Order", sans-serif',
                  fontWeight: 600,
                  fontSize: '1.5rem',
                  color: '#333b3e',
                }}
              >
                Bem-vindo de volta!
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"New Order", sans-serif',
                  fontWeight: 400,
                  fontSize: '0.875rem',
                  color: '#41484b',
                }}
              >
                Acesse sua plataforma de gestão inteligente.
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {error}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: '"New Order", sans-serif',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    color: '#41484b',
                    letterSpacing: '0.24px',
                  }}
                >
                  E-mail
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@email.com.br"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          component="img"
                          src={iconEmail}
                          alt=""
                          sx={{ width: 20, height: 20 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f1f4f3',
                      borderRadius: '8px',
                      height: 57,
                      '& fieldset': {
                        borderColor: '#c0c8cc',
                      },
                      '&:hover fieldset': {
                        borderColor: '#004e64',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#004e64',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontFamily: '"New Order", sans-serif',
                      fontSize: '1rem',
                      color: '#6b7280',
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"New Order", sans-serif',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      color: '#41484b',
                      letterSpacing: '0.24px',
                    }}
                  >
                    Senha
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"New Order", sans-serif',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      color: '#004e64',
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Esqueceu a senha?
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          component="img"
                          src={iconLock}
                          alt=""
                          sx={{ width: 20, height: 20 }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? (
                            <VisibilityOff sx={{ fontSize: 20, color: '#6b7280' }} />
                          ) : (
                            <Visibility sx={{ fontSize: 20, color: '#6b7280' }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f1f4f3',
                      borderRadius: '8px',
                      '& fieldset': {
                        borderColor: '#c0c8cc',
                      },
                      '&:hover fieldset': {
                        borderColor: '#004e64',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#004e64',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '1rem',
                      color: '#6b7280',
                    },
                  }}
                />
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{
                      color: '#c0c8cc',
                      '&.Mui-checked': {
                        color: '#004e64',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontFamily: '"New Order", sans-serif',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      color: '#41484b',
                    }}
                  >
                    Lembrar por 30 dias
                  </Typography>
                }
              />

              <Box
                component="button"
                type="submit"
                disabled={isLoading}
                sx={{
                  backgroundColor: '#004e64',
                  boxShadow: '0px 1px 1px rgba(0,0,0,0.05)',
                  borderRadius: '8px',
                  py: 2,
                  px: 0,
                  width: '100%',
                  border: 'none',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.7 : 1,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: isLoading ? '#004e64' : '#003a4d',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"New Order", sans-serif',
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    color: '#ffffff',
                    textAlign: 'center',
                  }}
                >
                  {isLoading ? 'Entrando...' : 'Entrar na plataforma'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ py: 4, position: 'relative' }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  right: 0,
                  borderTop: '1px solid #c0c8cc',
                }}
              />
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: '#f7faf9',
                  px: 2,
                  mx: 'auto',
                  width: 'fit-content',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"New Order", sans-serif',
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    color: '#41484b',
                  }}
                >
                  Não tem uma conta?{' '}
                  <Box
                    component={RouterLink}
                    to="/register"
                    sx={{
                      color: '#004e64',
                      textDecoration: 'none',
                      fontWeight: 600,
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Começar agora
                  </Box>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
